import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { AcceptInvite } from './AcceptInvite';
import { SignInBrand } from './SignIn';

/** `/sign-up`; with `?invite=<token>` the invite's accept flow (M7 slice 3), which signs up through the invite. */
export function SignUp() {
  const [params] = useSearchParams();
  const invite = params.get('invite');
  return invite ? <AcceptInvite token={invite} /> : <SignUpForm />;
}

function SignUpForm() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signUp(email, password, displayName);
      navigate('/orgs', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, 'internal_error', 'Something went wrong.', ''));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin">
      <form onSubmit={onSubmit} className="card" aria-labelledby="signup-title">
        <SignInBrand />
        <h1 id="signup-title">Create your account</h1>
        <p className="lede">One account for every organization you belong to.</p>
        <label>
          Email
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required />
          {error?.fieldMessage('email') && <span className="field-error">{error.fieldMessage('email')}</span>}
        </label>
        <label>
          Name
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoComplete="name" required />
          {error?.fieldMessage('displayName') && <span className="field-error">{error.fieldMessage('displayName')}</span>}
        </label>
        <label>
          Password
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" minLength={10} required />
          {error?.fieldMessage('password') && <span className="field-error">{error.fieldMessage('password')}</span>}
        </label>
        {error && !error.details?.length && (
          <p className="error" role="alert">
            {error.message}
            {error.requestId ? ` (request ${error.requestId})` : ''}
          </p>
        )}
        <button type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create account'}
        </button>
        <div className="links">
          <p>
            Already have an account? <Link to="/sign-in">Sign in</Link>
          </p>
        </div>
      </form>
    </div>
  );
}
